import { TextFieldComponent } from "components/basic/TextField";
import { SelectComponent } from "components/basic/Select";
import { ButtonRound } from "components/basic/Buttons";
import { useCallback, useEffect, useMemo } from "react";
import { fetchedAddressByPostcode } from "./store/addressSlice";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
type Props = {
  handleChange: (e: React.FormEvent<any>) => void;
  setFieldValue: Function;
  handleBlur: (e: React.FocusEvent<any, Element>) => void;
  values?: any;
  errors?: any;
  touched?: any;
  onSelectChange?: (option: any) => void;
  selectErrorMsg?: string;
  selectActionType?: "setInputFields" | "select" | "custom";
  newAddressView?: boolean;
};

export const Address = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  onSelectChange,
  selectErrorMsg = "",
  selectActionType = "setInputFields",
  newAddressView = false,
}: Props) => {
  const dispatch = useAppDispatch();

  const isManualAddress = useMemo(() => {
    return !!values?.isManualAddress;
  }, [values]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        !isManualAddress &&
        values.postcode &&
        values.postcode.length > 1 &&
        !errors.postcode
      ) {
        dispatch(fetchedAddressByPostcode(values.postcode));
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [values.postcode, errors.postcode, isManualAddress]);

  const addresses = useAppSelector((state) => state.address.addresses);

  const addressOptions = useMemo(() => {
    return addresses.map((address) => address.label);
  }, [addresses]);

  const handleAddressSelectChange = useCallback(
    (e: React.FormEvent<HTMLSelectElement>) => {
      const value = e.currentTarget.value;
      const option = addresses.find((address) => address.label === value);

      switch (selectActionType) {
        case "setInputFields": {
          if (option) {
            const { info } = option;
            
            // Always set address1, address2, address3, address4 regardless of address format
            setFieldValue("address1", info.line1 || "", true);
            setFieldValue("address2", info.line2 || "", true);
            setFieldValue("address3", info.town || "", true);
            setFieldValue("address4", info.county || "", true);
            
            if (newAddressView) {
              // For finance applications, we need to parse the address into more specific fields
              const line1Parts = (info.line1 || "").split(" ");
              const buildingNumber = line1Parts.length > 0 && !isNaN(Number(line1Parts[0])) 
                ? line1Parts[0] 
                : "";
              const street = line1Parts.length > 0 
                ? line1Parts.slice(buildingNumber ? 1 : 0).join(" ") 
                : info.line1 || "";
              
              setFieldValue("buildingNumber", buildingNumber || "", true);
              setFieldValue("street", street || "", true);
              setFieldValue("buildingName", "", true);
              setFieldValue("flatNumber", "", true);
              setFieldValue("locality", info.line2 || "", true);
              setFieldValue("townOrCity", info.town || "", true);
              setFieldValue("county", info.county || "", true);
            }
            
            // formik can't validate values so use a timer to trigger it
            setTimeout(() => {
              setFieldValue("isManualAddress", true);
            }, 0);
          }
          break;
        }
        case "select": {
          handleChange(e);
          break;
        }
        default: {
          break;
        }
      }
      if (onSelectChange) {
        onSelectChange(option);
      }
    },
    [selectActionType, onSelectChange, addresses, setFieldValue, handleChange, newAddressView]
  );

  // Update address1-4 fields whenever the detailed address fields change
  useEffect(() => {
    if (newAddressView && values?.isManualAddress) {
      // Only update when manual address entry is active and using new address format
      
      // Construct address1 from building details and street
      let address1 = '';
      if (values.flatNumber) {
        address1 += values.flatNumber + ' ';
      }
      if (values.buildingName) {
        address1 += values.buildingName + ' ';
      }
      if (values.buildingNumber) {
        address1 += values.buildingNumber + ' ';
      }
      if (values.street) {
        address1 += values.street;
      }
      
      // Set address fields if there's content to set
      if (address1.trim()) {
        setFieldValue("address1", address1.trim(), false);
      }
      
      if (values.locality) {
        setFieldValue("address2", values.locality, false);
      }
      
      if (values.townOrCity) {
        setFieldValue("address3", values.townOrCity, false);
      }
      
      if (values.county) {
        setFieldValue("address4", values.county, false);
      }
    }
  }, [
    newAddressView, 
    values?.isManualAddress,
    values?.flatNumber, 
    values?.buildingName,
    values?.buildingNumber,
    values?.street,
    values?.locality,
    values?.townOrCity,
    values?.county,
    setFieldValue
  ]);

  return (
    <>
      <TextFieldComponent
        className="w-full"
        label="Postcode"
        placeholder="Postcode"
        name="postcode"
        value={values?.postcode}
        errorMsg={touched.postcode && errors?.postcode}
        onBlur={handleBlur}
        onChange={(e: React.FormEvent<HTMLInputElement>) => {
          setFieldValue("postcode", e.currentTarget.value?.toUpperCase());
        }}
      ></TextFieldComponent>
      {isManualAddress ? (
        <>
          <TextFieldComponent
            name="company"
            value={values?.company}
            errorMsg={touched.company && errors?.company}
            onBlur={handleBlur}
            onChange={handleChange}
            className="w-full"
            label="Company Name (If applicable)"
            placeholder="Company Name (If applicable)"
          ></TextFieldComponent>
          
          {newAddressView ? (
            // Detailed address fields for finance application
            <>
              <div className="grid grid-cols-2 gap-4">
                <TextFieldComponent
                  name="flatNumber"
                  value={values?.flatNumber}
                  errorMsg={touched.flatNumber && errors?.flatNumber}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full"
                  label="Flat Number"
                  placeholder="Flat Number"
                ></TextFieldComponent>
                <TextFieldComponent
                  name="buildingNumber"
                  value={values?.buildingNumber}
                  errorMsg={touched.buildingNumber && errors?.buildingNumber}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full"
                  label="Building Number"
                  placeholder="Building Number"
                ></TextFieldComponent>
              </div>
              
              <TextFieldComponent
                name="buildingName"
                value={values?.buildingName}
                errorMsg={touched.buildingName && errors?.buildingName}
                onBlur={handleBlur}
                onChange={handleChange}
                className="w-full"
                label="Building Name"
                placeholder="Building Name"
              ></TextFieldComponent>
              
              <TextFieldComponent
                name="street"
                value={values?.street}
                errorMsg={touched.street && errors?.street}
                onBlur={handleBlur}
                onChange={handleChange}
                className="w-full"
                label="Street"
                placeholder="Street"
              ></TextFieldComponent>
              
              <TextFieldComponent
                name="locality"
                value={values?.locality}
                errorMsg={touched.locality && errors?.locality}
                onBlur={handleBlur}
                onChange={handleChange}
                className="w-full"
                label="Locality"
                placeholder="Locality"
              ></TextFieldComponent>
              
              <TextFieldComponent
                name="townOrCity"
                value={values?.townOrCity}
                errorMsg={touched.townOrCity && errors?.townOrCity}
                onBlur={handleBlur}
                onChange={handleChange}
                className="w-full"
                label="Town/City"
                placeholder="Town/City"
              ></TextFieldComponent>
              
              <TextFieldComponent
                name="county"
                value={values?.county}
                errorMsg={touched.county && errors?.county}
                onBlur={handleBlur}
                onChange={handleChange}
                className="w-full"
                label="County"
                placeholder="County"
              ></TextFieldComponent>
            </>
          ) : (
            // Standard address fields
            <>
              <TextFieldComponent
                name="address1"
                value={values?.address1}
                errorMsg={touched.address1 && errors?.address1}
                onBlur={handleBlur}
                onChange={handleChange}
                className="w-full"
                label="First line of address (including house number)"
                placeholder="First line of address (including house number)"
              ></TextFieldComponent>
              <TextFieldComponent
                name="address2"
                value={values?.address2}
                errorMsg={touched.address2 && errors?.address2}
                onBlur={handleBlur}
                onChange={handleChange}
                className="w-full"
                label="Second line of address"
                placeholder="Second line of address"
              ></TextFieldComponent>
              <TextFieldComponent
                name="address3" // city
                value={values?.address3}
                errorMsg={touched.address3 && errors?.address3}
                onBlur={handleBlur}
                onChange={handleChange}
                className="w-full"
                label="Town/city"
                placeholder="Town/city"
              ></TextFieldComponent>
              <TextFieldComponent
                name="address4" // county
                value={values?.address4}
                errorMsg={touched.address4 && errors?.address4}
                onBlur={handleBlur}
                onChange={handleChange}
                className="w-full"
                label="County"
                placeholder="County"
              ></TextFieldComponent>
            </>
          )}
        </>
      ) : (
        <SelectComponent
          label="Address"
          options={addressOptions}
          disabled={addresses.length === 0}
          defaultValue={values.address}
          onChange={handleAddressSelectChange}
          errorMsg={selectErrorMsg}
        ></SelectComponent>
      )}
      <ButtonRound
        text={isManualAddress ? "Search address" : "Can't find the address?"}
        type="success"
        className="mt-8 md:w-1/2 w-full mx-auto"
        onClick={(e) => {
          e.preventDefault();
          setFieldValue("isManualAddress", !isManualAddress);
        }}
      ></ButtonRound>
    </>
  );
};
